import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, Tag, ArrowRight, Search, Rss } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SchemaOrg } from '@/components/utils/SchemaOrg';
import { allBlogPostsArray } from '@/data/blogData';

const categories = ['All', ...new Set(allBlogPostsArray.map(post => post.category))];
const popularTags = [...new Set(allBlogPostsArray.flatMap(post => post.tags))].slice(0, 8);


const BlogPage = ({ onActionClick }) => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const pageTitle = "WebFiny Blog: Insights on PDF APIs & Document Automation";
  const pageDescription = "Stay updated with the latest news, articles, tutorials, and insights from WebFiny on PDF technology, document automation, API development, and industry trends.";
  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  const filteredPosts = allBlogPostsArray.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "WebFiny Blog",
    "description": pageDescription,
    "url": canonicalUrl,
    "publisher": {
      "@type": "Organization",
      "name": "WebFiny",
      "logo": {
        "@type": "ImageObject",
        "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
      }
    },
    "blogPost": filteredPosts.map(post => ({
      "@type": "BlogPosting",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${window.location.origin}/blog/${post.slug}`
      },
      "headline": post.title,
      "image": post.image,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": post.author.split(',')[0] 
      },
      "publisher": {
        "@type": "Organization",
        "name": "WebFiny",
        "logo": {
          "@type": "ImageObject",
          "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
        }
      },
      "description": post.excerpt,
      "keywords": post.tags.join(", ")
    }))
  };
  
  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[blogSchema]}
      />
      <div className="bg-gradient-to-b from-background to-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary mb-4">
              WebFiny Insights Hub
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Your source for the latest articles, tutorials, and news on PDF technology, document automation, and API best practices.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-8 md:gap-12">
            <main className="lg:col-span-8">
              {filteredPosts.length > 0 ? (
                <div className="space-y-10 md:space-y-12">
                  {filteredPosts.map((post, index) => (
                    <motion.article
                      key={post.slug}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="bg-card rounded-xl shadow-xl overflow-hidden hover:shadow-primary/20 transition-all duration-300 group"
                    >
                      <Link to={`/blog/${post.slug}`} className="block" aria-label={`Read more about ${post.title}`}>
                        <img 
                          src={post.image}
                          alt={`Featured image for blog post titled: ${post.title}`}
                          className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                          width="800"
                          height="400"
                         src="https://images.unsplash.com/photo-1504983875-d3b163aba9e6" />
                      </Link>
                      <div className="p-6 md:p-8">
                        <div className="flex items-center text-sm text-muted-foreground mb-3 space-x-4">
                          <span className="flex items-center"><Calendar className="w-4 h-4 mr-1.5 text-primary" /> {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          <span className="flex items-center"><User className="w-4 h-4 mr-1.5 text-primary" /> {post.author.split(',')[0]}</span>
                          <span className="hidden sm:flex items-center"><Tag className="w-4 h-4 mr-1.5 text-primary" /> {post.category}</span>
                        </div>
                        <Link to={`/blog/${post.slug}`} aria-label={`Read full article: ${post.title}`}>
                          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                            {post.title}
                          </h2>
                        </Link>
                        <p className="text-muted-foreground mb-5 leading-relaxed">{post.excerpt}</p>
                        <div className="flex flex-wrap items-center justify-between">
                          <Link to={`/blog/${post.slug}`} className="inline-flex items-center text-primary font-medium hover:underline group" aria-label={`Continue reading: ${post.title}`}>
                            Read More 
                            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform duration-300" />
                          </Link>
                          <span className="text-sm text-muted-foreground mt-2 sm:mt-0">{post.readTime}</span>
                        </div>
                        <div className="mt-4 pt-4 border-t border-border">
                          {post.tags.slice(0, 3).map(tag => (
                            <Button key={tag} variant="outline" size="sm" className="mr-2 mb-2 text-xs cursor-pointer hover:bg-accent/50" onClick={() => { setSearchTerm(tag); setSelectedCategory('All'); }} aria-label={`Filter posts by tag: ${tag}`}>
                              #{tag}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-card rounded-xl shadow-lg">
                  <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-2xl font-semibold text-foreground mb-2">No Posts Found</h3>
                  <p className="text-muted-foreground">Try adjusting your search or category filters.</p>
                </div>
              )}
              
              {filteredPosts.length > 5 && (
                 <div className="mt-12 flex justify-center space-x-2">
                    <Button variant="outline" onClick={() => onActionClick("Previous Page Blog")} aria-label="Go to previous page of blog posts">Previous</Button>
                    <Button variant="outline" onClick={() => onActionClick("Next Page Blog")} aria-label="Go to next page of blog posts">Next</Button>
                </div>
              )}

            </main>

            <aside className="lg:col-span-4 space-y-8 md:space-y-10">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-card p-6 rounded-xl shadow-lg"
              >
                <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                  <Search className="w-5 h-5 mr-2 text-primary" /> Search Articles
                </h3>
                <div className="relative">
                  <Input 
                    type="text" 
                    placeholder="Search blog..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pr-10 bg-background/70 border-border focus:border-primary"
                    aria-label="Search blog articles"
                  />
                  <Search className="w-4 h-4 text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-card p-6 rounded-xl shadow-lg"
              >
                <h3 className="text-xl font-semibold text-foreground mb-4">Categories</h3>
                <ul className="space-y-2">
                  {categories.map(category => (
                    <li key={category}>
                      <Button 
                        variant={selectedCategory === category ? "secondary" : "ghost"} 
                        className={`w-full justify-start ${selectedCategory === category ? 'font-semibold text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setSelectedCategory(category)}
                        aria-label={`Filter posts by category: ${category}`}
                      >
                        {category}
                      </Button>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-card p-6 rounded-xl shadow-lg"
              >
                <h3 className="text-xl font-semibold text-foreground mb-4">Popular Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map(tag => (
                    <Button 
                      key={tag} 
                      variant="outline" 
                      size="sm" 
                      className="text-xs hover:bg-accent/50"
                      onClick={() => { setSearchTerm(tag); setSelectedCategory('All'); }}
                      aria-label={`Filter posts by tag: ${tag}`}
                    >
                      #{tag}
                    </Button>
                  ))}
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="bg-gradient-to-br from-primary to-accent p-6 rounded-xl shadow-lg text-primary-foreground"
              >
                <h3 className="text-xl font-semibold mb-3 flex items-center"><Rss className="w-5 h-5 mr-2" /> Subscribe to Our Newsletter</h3>
                <p className="text-sm opacity-90 mb-4">Get the latest WebFiny news, articles, and API updates delivered straight to your inbox.</p>
                <form onSubmit={(e) => { e.preventDefault(); onActionClick("Newsletter Subscription"); }} className="space-y-3">
                  <Input type="email" placeholder="Enter your email" className="bg-background/20 border-transparent placeholder:text-primary-foreground/70 focus:bg-background/30" aria-label="Email address for newsletter subscription" />
                  <Button type="submit" variant="secondary" className="w-full bg-background/90 text-primary hover:bg-background">Subscribe</Button>
                </form>
              </motion.div>

            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage;